import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Check,
  BookOpen,
  TrendingUp,
  Repeat2,
  Trophy,
  BarChart2,
  Star,
  BookMarked,
} from "lucide-react";
import { API_BASE } from "../config";
import "../styles/landingPage.css";
import apiFetch from "../apiFetch";

const FEATURES = [
  {
    icon: <TrendingUp size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#14b8a6,#0d9488)",
    title: "Vocabulary Matching",
    desc: "Climb ELO tiers, match words to definitions.",
  },
  {
    icon: <Repeat2 size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#6366f1,#4f46e5)",
    title: "Synonyms & Antonyms",
    desc: "Deepen word knowledge through contrast.",
  },
  {
    icon: <BookOpen size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#f97316,#ea580c)",
    title: "Comprehension Tests",
    desc: "Adaptive passages and multiple-choice.",
  },
  {
    icon: <Trophy size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#eab308,#ca8a04)",
    title: "Ranked Mode",
    desc: "Compete and climb the seasonal board.",
  },
  {
    icon: <Star size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#a855f7,#9333ea)",
    title: "Vocabulary Tests",
    desc: "Weighted to your personal weak spots.",
  },
  {
    icon: <BarChart2 size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#ec4899,#db2777)",
    title: "Progress Reports",
    desc: "ELO history and detailed session stats.",
  },
  {
    icon: <BookMarked size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#6366f1,#3730a3)",
    title: "Book Reading",
    desc: "Chapters with AI passage explanations.",
  },
  {
    icon: <BookOpen size={16} color="#fff" />,
    bg: "linear-gradient(135deg,#0ea5e9,#0284c7)",
    title: "Book Comprehension",
    desc: "Select a chapter, answer deep questions.",
  },
];

const PLANS = [
  {
    label: "Free Trial",
    price: "£0",
    period: "first month",
    cta: "Start Free →",
    link: "/register",
    featured: false,
    features: [
      "All games & modes",
      "Full progress tracking",
      "No credit card needed",
    ],
  },
  {
    label: "Monthly",
    price: "£10",
    period: "per month",
    cta: "Go Monthly →",
    link: "/register",
    featured: false,
    features: [
      "All games & modes",
      "Full progress tracking",
      "Cancel any month",
    ],
  },
  {
    label: "Annual",
    price: "£100",
    period: "per year",
    save: "save £20",
    cta: "Get Annual →",
    link: "/register",
    featured: true,
    features: [
      "All games & modes",
      "Full progress tracking",
      "Cancel any month",
      "Full refund if cancelled",
      "Save £20 vs monthly",
    ],
  },
];

const FAQS = [
  {
    q: "Do I need a credit card to start?",
    a: "No. Sign up and enjoy the full platform free for 30 days — no payment details required.",
  },
  {
    q: "What happens when the free month ends?",
    a: "You'll choose a plan. Until you subscribe your account is paused, but your progress is never deleted.",
  },
  {
    q: "How does the annual refund work?",
    a: "Cancel in month 1 → full £100 refund. Cancel in month 2 → we charge £10 and refund £90. After two months the fee is non-refundable, but you keep full access for the year.",
  },
  {
    q: "Can I switch between monthly and annual?",
    a: "Yes — upgrade anytime. Remaining monthly days are credited toward your annual plan.",
  },
];

function FaqItem({ q, a, dark }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`lp-faq-item${open ? " open" : ""}${dark ? " lp-faq-item--dark" : ""}`}
    >
      <button className="lp-faq-q" onClick={() => setOpen((o) => !o)}>
        {q}
        <ChevronDown size={16} className="lp-faq-chevron" />
      </button>
      <p className="lp-faq-a">{a}</p>
    </div>
  );
}

export default function LandingPage() {
  const [wordCount, setWordCount] = useState("5,000+");

  useEffect(() => {
    document.title = "Owllet Reading Quest — Master Reading Through Play";
    apiFetch(`/api/vocabulary`)
      .then((r) => r.json())
      .then((data) => {
        const words = Array.isArray(data) ? data : data.words || [];
        if (words.length > 0) setWordCount(words.length.toLocaleString() + "+");
      })
      .catch(() => {});
  }, []);

  return (
    <div className="lp">
      {/* ── NAV ── */}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <img
            src="/images/owlletLogo.png"
            alt="Owllet"
            className="lp-nav-logo"
          />
          <span className="lp-nav-name">Owllet Reading Quest</span>
        </div>
        <div className="lp-nav-actions">
          <Link to="/projLoginPage" className="lp-nav-login">
            Log In
          </Link>
          <Link to="/register" className="lp-nav-cta">
            Start Free
          </Link>
        </div>
      </nav>

      {/* ══════════════════════════════════════════
          HERO — 3-column layout
          ══════════════════════════════════════════ */}
      <section className="lp-hero">
        <div className="lp-hero-grid">
          {/* ── LEFT: Game modes ── */}
          <div className="lp-hero-col lp-hero-col--left">
            <p className="lp-col-eyebrow">What's Inside</p>
            <h3 className="lp-col-heading">8 Game Modes</h3>
            <div className="lp-feat-list">
              {FEATURES.map((f) => (
                <div key={f.title} className="lp-feat-row">
                  <div className="lp-feat-icon" style={{ background: f.bg }}>
                    {f.icon}
                  </div>
                  <div className="lp-feat-body">
                    <p className="lp-feat-title">{f.title}</p>
                    <p className="lp-feat-desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── MIDDLE: Hero ── */}
          <div className="lp-hero-inner">
            <div className="lp-hero-badge">
              <Star size={11} /> Free for your first month — no card needed
            </div>

            <img
              src="/images/owlletLogo.png"
              alt="Owllet owl mascot"
              className="lp-hero-owl"
            />

            <h1 className="lp-hero-title">
              Master Reading &amp; Vocabulary
              <br />
              <span>Through Play</span>
            </h1>

            <p className="lp-hero-sub">
              Games, ranked challenges and adaptive vocabulary training —<br />
              built to make every learner love reading.
            </p>

            <div className="lp-hero-btns">
              <Link to="/register" className="lp-btn-primary">
                Start Free — 1 Month ✦
              </Link>
              <Link to="/projLoginPage" className="lp-btn-ghost">
                Log In →
              </Link>
            </div>

            <div className="lp-hero-trust">
              {[
                "No credit card",
                "Cancel anytime",
                "Full access from day one",
              ].map((t) => (
                <span key={t} className="lp-trust-item">
                  <span className="lp-trust-dot" />
                  {t}
                </span>
              ))}
            </div>

            <div className="lp-hero-stats">
              {[
                { value: wordCount, label: "Vocab words" },
                { value: "5", label: "Tier levels" },
                { value: "6+", label: "Game modes" },
                { value: "£0", label: "First month" },
              ].map((s) => (
                <div key={s.label} className="lp-hero-stat">
                  <span className="lp-hero-stat-value">{s.value}</span>
                  <span className="lp-hero-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Subscription ── */}
          <div className="lp-hero-col lp-hero-col--right">
            <p className="lp-col-eyebrow">Pricing</p>
            <h3 className="lp-col-heading">Simple, fair pricing</h3>

            <div className="lp-plan-cards">
              {PLANS.map((p) => (
                <div
                  key={p.label}
                  className={`lp-plan-card${p.featured ? " lp-plan-card--featured" : ""}`}
                >
                  {p.featured && (
                    <div className="lp-plan-badge">★ Best Value</div>
                  )}
                  <div
                    className={`lp-plan-header${p.featured ? " lp-plan-header--featured" : ""}`}
                  >
                    <span className="lp-plan-label">{p.label}</span>
                    <div className="lp-plan-price">
                      <span
                        className={`lp-plan-amount${p.featured ? " lp-plan-amount--featured" : ""}`}
                      >
                        {p.price}
                      </span>
                      <span className="lp-plan-period">
                        /{p.period}
                        {p.save && (
                          <span className="lp-pricing-save">{p.save}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <ul className="lp-plan-feats">
                    {p.features.map((feat) => (
                      <li key={feat}>
                        <Check size={11} className="lp-plan-check" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={p.link}
                    className={`lp-plan-cta${p.featured ? " lp-plan-cta--primary" : " lp-plan-cta--outline"}`}
                  >
                    {p.cta}
                  </Link>
                </div>
              ))}
            </div>

            <div className="lp-refund-pill">
              <span>🛡️</span>
              <p>
                Annual plan: cancel anytime after your free Trial, will be
                refunded for remaining months.
              </p>
            </div>
          </div>
        </div>

        {/* ── FAQ — horizontal strip at bottom of hero ── */}
        <div className="lp-hero-faq" id="faq">
          <div className="lp-hero-faq-head">
            <p className="lp-col-eyebrow">Questions</p>
            <h2 className="lp-hero-faq-title">Common questions</h2>
          </div>
          <div className="lp-faq-grid">
            {FAQS.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} dark />
            ))}
          </div>
        </div>

        <div
          className="lp-hero-scroll"
          onClick={() =>
            document
              .getElementById("cta")
              ?.scrollIntoView({ behavior: "smooth" })
          }
        >
          <ChevronDown size={26} />
        </div>
      </section>

      {/* ── CTA banner ── */}
      <div className="lp-cta-banner" id="cta">
        <img src="/images/owlletLogo.png" alt="" className="lp-cta-owl" />
        <h2>Ready to start?</h2>
        <p>Your first month is completely free. No card needed.</p>
        <Link to="/register" className="lp-btn-primary">
          Create Free Account →
        </Link>
        <p className="lp-cta-note">Cancel anytime · Full access from day one</p>
      </div>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-links">
          <a href="/privacy-policy">Privacy Policy</a>
          <a href="/terms">Terms &amp; Conditions</a>
          <a href="/cookie-policy">Cookie Policy</a>
          <a href="/safeguarding">Safeguarding</a>
          <a href="/contact">Contact</a>
        </div>
        <p className="lp-footer-copy">
          © {new Date().getFullYear()} Owllet Reading Quest. All rights
          reserved.
        </p>
      </footer>
    </div>
  );
}
